/** Display labels for messaging (English source strings; render with `t()`). */
import { msg } from '@/i18n';
import type {
  AnnouncementAudience,
  ConversationRow,
  RecordThreadType,
} from '@/lib/api/messaging-types';

export const AUDIENCE_LABELS: Record<string, string> = {
  whole_project: msg('Whole project'),
  contractor_team: msg('Contractor team'),
  owner_side: msg('Owner side'),
  crew: msg('Crew'),
  workers: msg('All workers'),
};

export const AUDIENCE_HINTS: Record<string, string> = {
  whole_project: msg('Owner side, contractor, quantity surveyors and consultants'),
  contractor_team: msg('The contractor and its subcontractors'),
  owner_side: msg('Owner, owner’s PM, the employer’s QS and consultants'),
  crew: msg('The site team and the workers it engaged'),
  workers: msg('Every worker engaged on this project'),
};

export const RECORD_LABELS: Record<RecordThreadType, string> = {
  rfi: msg('RFI'),
  variation: msg('Variation'),
  change_request: msg('Change request'),
  selection: msg('Selection'),
  certificate: msg('Certificate'),
  snag: msg('Snag'),
  ncr: msg('NCR'),
  purchase_order: msg('Purchase order'),
  subcontract: msg('Subcontract'),
};

export const KIND_FILTERS = [
  { value: 'all', label: msg('All') },
  { value: 'channel', label: msg('Project') },
  { value: 'direct', label: msg('Direct') },
  { value: 'record', label: msg('Records') },
  { value: 'announcement', label: msg('Announcements') },
  { value: 'supplier', label: msg('Suppliers') },
] as const;
export type KindFilter = (typeof KIND_FILTERS)[number]['value'];

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The title a person sees for a conversation (channels and announcements in their language). */
export function conversationTitle(
  row: Pick<ConversationRow, 'kind' | 'audience' | 'title' | 'display_title'> & {
    supplier_name?: string | null;
  },
  t: Translate,
): string {
  if (row.kind === 'channel') {
    if (row.audience === 'crew') {
      const company = row.title.split('·').slice(1).join('·').trim();
      return company ? `${t('Crew')} · ${company}` : t('Crew');
    }
    return t(AUDIENCE_LABELS[row.audience] ?? row.title);
  }
  if (row.kind === 'announcement') {
    return `${t('Announcements')} · ${t(AUDIENCE_LABELS[row.audience] ?? row.audience)}`;
  }
  if (row.kind === 'supplier' && row.supplier_name) {
    return `${row.display_title || row.title} · ${row.supplier_name}`;
  }
  return row.display_title || row.title;
}

export function kindLabel(kind: string): string {
  switch (kind) {
    case 'channel':
      return msg('Project channel');
    case 'direct':
      return msg('Direct message');
    case 'record':
      return msg('Discussion');
    case 'announcement':
      return msg('Announcements');
    case 'supplier':
      return msg('Supplier thread');
    default:
      return msg('Conversation');
  }
}

/** Who may post where (the server decides; this only offers the sensible choices). */
export function audiencesFor(partyRole: string): AnnouncementAudience[] {
  if (partyRole === 'owner') return ['owner_side', 'whole_project'];
  if (partyRole === 'contractor') return ['workers', 'contractor_team', 'whole_project'];
  return [];
}
